import { Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useGetDicListQuery } from '@/api/common';
import AppAutoCompleteMenu from '@/common/components/form-inputs/AppAutoCompleteMenu';
import AppMultipleDropDownMenu from '@/common/components/form-inputs/AppMultipleDropDownMenu';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditEducationWorkLiveForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const { data: educationDicList } = useGetDicListQuery('education', {
			refetchOnMountOrArgChange: true,
		});
		const { data: workStatusDicList } = useGetDicListQuery('work_status', {
			refetchOnMountOrArgChange: true,
		});
		const { data: livingStatusDicList } = useGetDicListQuery('living_status', {
			refetchOnMountOrArgChange: true,
		});
		const { data: financialConditionDicList } = useGetDicListQuery(
			'financial_condition',
			{ refetchOnMountOrArgChange: true }
		);

		const defaultValues: DefaultValues<any> = {
			hasEmergencyContact: null,
		};

		useEffect(() => {
			if (details) methods.reset(details);
		}, [details]);

		const formSchema = Yup.object().shape({});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.educationalWorkLive')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='education'
									label={t('preAdmission.zh_education')}
									options={(educationDicList?.data || []).map(
										(item: { labelZh: any }) => {
											return {
												...item,
												name: item.labelZh,
											};
										}
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppAutoCompleteMenu
									control={methods.control}
									name='workStatus'
									label={t('preAdmission.occupation')}
									options={(workStatusDicList?.data || []).map(
										(item: { labelZh: any }) => {
											return {
												...item,
												name: item.labelZh,
											};
										}
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='livingStatus'
									label={t('preAdmission.liveStatus')}
									options={(livingStatusDicList?.data || []).map(
										(item: { labelZh: any }) => {
											return {
												...item,
												name: item.labelZh,
											};
										}
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppMultipleDropDownMenu
									control={methods.control}
									name='financialConditionList'
									label={t('preAdmission.sourceFinance')}
									options={(financialConditionDicList?.data || []).map(
										(item: { labelZh: any }) => {
											return {
												...item,
												name: item.labelZh,
											};
										}
									)}
								/>
							</Grid>
						</Grid>

						<Divider sx={{ marginBottom: '16px' }} />
					</form>
				</FormProvider>
			</>
		);
	}
);

EditEducationWorkLiveForm.displayName = 'EditEducationWorkLiveForm';

export default EditEducationWorkLiveForm;
